import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';

const NotificationsContext = createContext(null);
const EMPTY = { items: [], unread_count: 0, total: 0 };

export const NotificationsProvider = ({ children }) => {
  const { user, userType } = useAuth();
  const identity = user?.id ? `${userType}:${user.id}` : '';
  const current = useRef(identity);
  current.current = identity;
  const request = useRef(null);
  const [snapshot, setSnapshot] = useState({ owner: '', ...EMPTY });
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(async () => {
    if (!identity) return;
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit: 10 }, signal: controller.signal });
      if (current.current === identity && !controller.signal.aborted) {
        setSnapshot({ owner: identity, ...data }); setError(false); setVersion((v) => v + 1);
      }
    } catch (e) {
      if (!controller.signal.aborted && current.current === identity) setError(true);
    } finally { if (!controller.signal.aborted && current.current === identity) setLoading(false); }
  }, [identity]);

  useEffect(() => {
    setSnapshot({ owner: identity, ...EMPTY }); setError(false); setLoading(false);
    if (!identity) return undefined;
    refresh();
    const activeRefresh = () => { if (document.visibilityState === 'visible') refresh(); };
    const timer = setInterval(activeRefresh, 30000);
    window.addEventListener('focus', activeRefresh);
    document.addEventListener('visibilitychange', activeRefresh);
    return () => {
      clearInterval(timer); request.current?.abort();
      window.removeEventListener('focus', activeRefresh);
      document.removeEventListener('visibilitychange', activeRefresh);
    };
  }, [identity, refresh]);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    if (current.current === identity) await refresh();
  };
  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    if (current.current === identity) await refresh();
  };
  const data = snapshot.owner === identity ? snapshot : EMPTY;
  return <NotificationsContext.Provider value={{ ...data, identity, error, loading, version, refresh, markRead, markAllRead }}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => useContext(NotificationsContext);